// src/features/notifications/components/NotificationBell.jsx
import React, { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, Box, Button, Divider, FormControlLabel, IconButton, List, ListItemButton,
  Popover, Skeleton, Switch, Tooltip, Typography,
} from '@mui/material';

import Icon from '../../../shared/components/Icon';
import { PriorityBadge, TypeChip } from '../../../shared/components/Badges';
import { uiText } from '../../../shared/constants/uiText';
import { timeAgo } from '../../../shared/utils/format';
import { useNotifications } from '../context/NotificationProvider';
import '../styles/notifications.css';

const priorityClass = priority => (['urgent', 'high'].includes(String(priority).toLowerCase())
  ? 'noti-priority-high'
  : String(priority).toLowerCase() === 'low' ? 'noti-priority-low' : 'noti-priority-normal');

const NotificationItem = memo(function NotificationItem({ entry, onOpen }) {
  return (
    <ListItemButton
      className={`noti-history-item${entry.isRead ? ' is-read' : ' is-unread'}`}
      onClick={() => onOpen(entry)}
      aria-label={`${entry.isRead ? 'Read' : 'Unread'} notification ${entry.ticketNo}: ${entry.subject}`}
      disableRipple
    >
      {!entry.isRead && <span className="noti-unread-dot" aria-hidden="true" />}
      <span className="noti-history-content">
        <span className="noti-history-topline">
          <span className="noti-ticket-id">{entry.ticketNo}</span>
          <span className="noti-history-time">{timeAgo(entry.receivedAt)}</span>
        </span>
        <span className="noti-history-item-subject">{entry.subject}</span>
        <span className="noti-history-meta">
          <TypeChip
            type={entry.category || 'unclassified'}
            typeMeta={entry.typeMeta || { label: 'Unclassified', icon: 'more', color: '#6b7280' }}
          />
          <PriorityBadge priority={entry.priority || 'unclassified'} />
        </span>
      </span>
    </ListItemButton>
  );
});

export default function NotificationBell({ onOpenTicket }) {
  const {
    enabled, toggle, history, unread, markAllRead, markTicketRead, loading, settingsSaving,
    historyError, historyLoading, hasMoreHistory, loadMoreHistory, retryInitialLoad,
  } = useNotifications();
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleItemClick = (entry) => {
    const ticketId = entry.ticketRef?._id;
    if (ticketId) {
      markTicketRead(entry.id);
      handleClose();
      if (onOpenTicket) onOpenTicket(ticketId);
      else navigate(`/tickets/${ticketId}`);
    }
  };

  return (
    <>
      <Tooltip title={uiText('NOTIFICATIONS_PANEL_TOGGLE')}>
        <IconButton
          className="btn-ghost noti-bell-button"
          aria-label="Notifications"
          onClick={handleOpen}
          disabled={loading}
        >
          <Badge badgeContent={unread > 99 ? '99+' : unread} color="error" invisible={unread <= 0}>
            <Icon name={enabled ? 'bell' : 'bell-off'} size={18} />
          </Badge>
        </IconButton>
      </Tooltip>
      {open && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ className: 'noti-popover-paper' }}
        >
        <Box className="noti-popover-header">
          <Typography variant="subtitle2">Notifications</Typography>
          <Box className="noti-header-actions">
            {unread > 0 && <Button size="small" onClick={markAllRead}>Mark all as read</Button>}
            <FormControlLabel
              control={<Switch checked={enabled} onChange={toggle} disabled={settingsSaving} size="small" />}
              label={settingsSaving ? 'Saving…' : enabled ? 'On' : 'Off'}
              labelPlacement="start"
            />
          </Box>
        </Box>
        <Divider />
        {loading ? (
          <Box className="noti-skeleton-list" role="status" aria-label="Loading notifications">
            {[0, 1, 2].map(item => <Box className="noti-skeleton-item" key={item}>
              <Skeleton variant="circular" width={7} height={7} />
              <Box sx={{ flex: 1 }}><Skeleton width="38%" /><Skeleton width="72%" /><Skeleton width="48%" /></Box>
            </Box>)}
          </Box>
        ) : historyError && history.length === 0 ? (
          <Box className="noti-popover-error" role="alert">
            <Typography variant="body2">{historyError}</Typography>
            <Button size="small" onClick={retryInitialLoad}>Retry</Button>
          </Box>
        ) : history.length === 0 ? (
          <Box className="noti-popover-empty">
            <Typography variant="body2" color="text.secondary">
              No notifications yet.
            </Typography>
          </Box>
        ) : (
          <List className="noti-history-list" dense disablePadding>
            {history.map(entry => (
              <NotificationItem key={`${entry.id}:${entry.isRead}`} entry={entry} onOpen={handleItemClick} />
            ))}
            {historyError && <Box className="noti-load-more-error" role="alert">
              {historyError} <Button size="small" onClick={loadMoreHistory}>Retry</Button>
            </Box>}
            {hasMoreHistory && <Box className="noti-load-more">
              <Button size="small" disabled={historyLoading} onClick={loadMoreHistory}>
                {historyLoading ? 'Loading…' : 'Load more'}
              </Button>
            </Box>}
          </List>
        )}
        </Popover>
      )}
    </>
  );
}
